import type { VentaPendienteMaster } from '../services/ventasSeguimientoService';

/** Rutas canónicas de la aplicación (paths relativos al router). */
export const ROUTES = {
  crm: {
    root: '/crm',
    contactos: {
      list: '/crm/contactos',
      nuevo: '/crm/contactos/nuevo',
      editar: (id: number | string) => `/crm/contactos/${encodeURIComponent(String(id))}`,
    },
    empresas: {
      list: '/crm/empresas',
      nuevo: '/crm/empresas/nuevo',
      editar: (id: number | string) => `/crm/empresas/${encodeURIComponent(String(id))}`,
    },
    pipeline: '/crm/pipeline',
    agenda: '/crm/agenda',
    oportunidades: {
      detalle: (id: number | string) => `/crm/oportunidades/${encodeURIComponent(String(id))}`,
    },
    kpis: '/crm/kpis',
    config: '/crm/config',
  },
  ventas: {
    facturas: {
      list: '/ventas/facturas',
      pos: '/ventas/facturas/pos',
      /** Formulario clásico con barra de atajos (FacturaForm) */
      nuevo: '/ventas/facturas/nuevo',
      clasico: '/ventas/facturas/clasico',
      /** Legacy: redirige a POS con `nroFormulario` en App.tsx */
      ver: (nroFormulario: number) => `/ventas/facturas/${nroFormulario}/ver`,
      /** Legacy: redirige a POS con `nroFormulario` y `editar=1` en App.tsx */
      editar: (nroFormulario: number) => `/ventas/facturas/${nroFormulario}/editar`,
    },
    pedidos: {
      list: '/ventas/pedidos',
      pos: '/ventas/pedidos/nuevo-pos',
      /** Legacy: redirige a `pos` en App.tsx */
      nuevo: '/ventas/pedidos/nuevo',
      ver: (pedidoId: number) => `/ventas/pedidos/${pedidoId}/ver`,
      editar: (pedidoId: number) => `/ventas/pedidos/${pedidoId}/editar`,
    },
    cotizaciones: {
      list: '/ventas/cotizaciones',
      pos: '/ventas/cotizaciones/nuevo-pos',
      /** Legacy: redirige a `pos` en App.tsx */
      nuevo: '/ventas/cotizaciones/nuevo',
      ver: (cotizacionId: number) => `/ventas/cotizaciones/${cotizacionId}/ver`,
      editar: (cotizacionId: number) => `/ventas/cotizaciones/${cotizacionId}/editar`,
    },
    notasCredito: {
      list: '/ventas/notas-credito',
      pos: '/ventas/notas-credito/nuevo-pos',
      ver: (nroFormulario: number) => `/ventas/notas-credito/${nroFormulario}`,
    },
    notasDebito: {
      list: '/ventas/notas-debito',
      pos: '/ventas/notas-debito/nuevo-pos',
      ver: (nroFormulario: number) => `/ventas/notas-debito/${nroFormulario}`,
    },
    remitos: {
      list: '/ventas/remitos',
      /** Alta remito con UI POS (misma ruta canónica). */
      pos: '/ventas/remitos/nuevo',
      nuevo: '/ventas/remitos/nuevo',
    },
    clientes: {
      list: '/ventas/clientes',
      nuevo: '/ventas/clientes/nuevo',
      editar: (nroCliente: number) => `/ventas/clientes/${nroCliente}`,
      storeAccessRequests: '/ventas/clientes/solicitudes-ecommerce',
      /** Ficha Info 360 (consulta comercial/crédito). No está en el menú lateral. */
      info: (nroCliente: number, options?: { returnTo?: string }) => {
        const base = `/ventas/clientes/${nroCliente}/info`;
        const returnTo = options?.returnTo?.trim();
        if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
          return base;
        }
        const params = new URLSearchParams();
        params.set('returnTo', returnTo);
        return `${base}?${params.toString()}`;
      },
    },
  },
  servicio: {
    root: '/servicio',
    turnos: {
      list: '/servicio/turnos',
      nuevo: '/servicio/turnos/nuevo',
      editar: (id: number | string) => `/servicio/turnos/${encodeURIComponent(String(id))}`,
    },
    organizador: '/servicio/organizador',
    ordenesTrabajo: {
      list: '/servicio/ordenes-reparacion',
      nuevo: '/servicio/ordenes-reparacion/nuevo',
      editar: (id: number | string) => `/servicio/ordenes-reparacion/${encodeURIComponent(String(id))}`,
    },
    tecnicos: {
      list: '/servicio/tecnicos',
      nuevo: '/servicio/tecnicos/nuevo',
      editar: (codigo: string) => `/servicio/tecnicos/${encodeURIComponent(codigo)}`,
    },
    vehiculos: {
      list: '/servicio/vehiculos',
      nuevo: '/servicio/vehiculos/nuevo',
      editar: (patente: string) => `/servicio/vehiculos/${encodeURIComponent(patente)}`,
    },
  },
  inventario: {
    productos: {
      list: '/inventario/productos',
      nuevo: '/inventario/productos/nuevo',
      editar: (codigoArticulo: string) => `/inventario/productos/${encodeURIComponent(codigoArticulo)}`,
      /** Ficha Info 360 (consulta operativa del artículo). No está en el menú lateral. */
      info: (codigoArticulo: string, options?: { returnTo?: string }) => {
        const base = `/inventario/productos/${encodeURIComponent(codigoArticulo)}/info`;
        const returnTo = options?.returnTo?.trim();
        if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
          return base;
        }
        const params = new URLSearchParams();
        params.set('returnTo', returnTo);
        return `${base}?${params.toString()}`;
      },
    },
  },
  tesoreria: {
    recibos: {
      list: '/tesoreria/recibos',
      nuevo: '/tesoreria/recibos/nuevo',
      /** Alta de recibo precargando cliente (seña / cobranza). */
      nuevoConCliente: (nroCliente: number, options?: { detalle?: string }) => {
        const params = new URLSearchParams();
        params.set('nroCliente', String(nroCliente));
        if (options?.detalle?.trim()) params.set('detalle', options.detalle.trim());
        return `/tesoreria/recibos/nuevo?${params.toString()}`;
      },
    },
    ordenPago: {
      list: '/tesoreria/orden-pago',
      nuevo: '/tesoreria/orden-pago/nuevo',
      ver: (nroFormulario: number) => `/tesoreria/orden-pago/${nroFormulario}`,
    },
  },
} as const;

export interface VentaPendienteLocationState {
  ventaPendiente?: VentaPendienteMaster;
}

/** Payload para armar una factura huérfana ARCA en el POS (sin pedir CAE nuevo). */
export interface AfipRescatePosPayload {
  idWsfe: string;
  idCompania: string;
  cuitEmisor: string;
  puntoVenta: number;
  codigoComprobanteAfip: number;
  numeroComprobante: number;
  letra?: string;
  cae?: string;
  fechaComprobante?: string;
  fechaVencimientoCae?: string;
  importeTotal?: string;
  impNeto?: string;
  impIva?: string;
  docNro?: string;
  docTipo?: number | null;
  /** Ruta de retorno (p. ej. compañía en sistema). */
  returnTo?: string;
}

export interface FacturaPosLocationState extends VentaPendienteLocationState {
  afipRescate?: AfipRescatePosPayload;
}

export type PedidoPosLocationState = VentaPendienteLocationState;

export type CotizacionPosLocationState = VentaPendienteLocationState;

export interface FacturaPosPathOptions {
  pedidoId?: number;
  cotizacionId?: number;
  facturaId?: number;
  ordenTrabajoId?: number;
}

export interface PedidoPosPathOptions {
  cotizacionId?: number;
}

/** Construye la URL de alta POS de factura con query de origen (si aplica). */
export function facturaPosPath(options?: FacturaPosPathOptions): string {
  const params = new URLSearchParams();
  if (options?.pedidoId && options.pedidoId > 0) {
    params.set('pedidoId', String(options.pedidoId));
  } else if (options?.ordenTrabajoId && options.ordenTrabajoId > 0) {
    params.set('ordenTrabajoId', String(options.ordenTrabajoId));
  } else if (options?.cotizacionId && options.cotizacionId > 0) {
    params.set('cotizacionId', String(options.cotizacionId));
  } else if (options?.facturaId && options.facturaId > 0) {
    params.set('facturaId', String(options.facturaId));
  }
  const query = params.toString();
  return query ? `${ROUTES.ventas.facturas.pos}?${query}` : ROUTES.ventas.facturas.pos;
}

/** Navegar al POS en modo rescate ARCA (usar con `navigate(path, { state: { afipRescate } })`). */
export function facturaPosRescatePath(): string {
  return ROUTES.ventas.facturas.pos;
}

/** URL POS para ver una factura existente (solo lectura). */
export function facturaPosVerPath(nroFormulario: number): string {
  return `${ROUTES.ventas.facturas.pos}?nroFormulario=${nroFormulario}`;
}

/** URL POS para editar una factura existente. */
export function facturaPosEditarPath(nroFormulario: number): string {
  return `${ROUTES.ventas.facturas.pos}?nroFormulario=${nroFormulario}&editar=1`;
}

/** Construye la URL de alta POS de pedido con query de origen (si aplica). */
export function pedidoPosPath(options?: PedidoPosPathOptions): string {
  const params = new URLSearchParams();
  if (options?.cotizacionId && options.cotizacionId > 0) {
    params.set('cotizacionId', String(options.cotizacionId));
  }
  const query = params.toString();
  return query ? `${ROUTES.ventas.pedidos.pos}?${query}` : ROUTES.ventas.pedidos.pos;
}
